export async function initSoundTouchNode(context, options = {}) {
	if (!context.soundtouchWorkletLoaded) {
		try {
			const path = window.location.pathname.split('/');
			const workletDir = path.slice(0, path.length - 1).join('/');
			await context.audioWorklet.addModule(`${workletDir}/soundtouchWorklet.js`);
			context.soundtouchWorkletLoaded = true;
		} catch (error) {
			console.error('Ошибка загрузки AudioWorklet:', error);
			throw error;
		}
	}
	
	return new SoundTouchNode(context, options);
}

export default class SoundTouchNode extends AudioWorkletNode {
  constructor(context, options = {}) {
    if (!context.soundtouchWorkletLoaded) {
      throw new Error('AudioWorklet должен быть загружен перед созданием SoundTouchNode');
    }

    const defaultOptions = {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [2]
    };
    
    super(context, 'soundtouch-processor', defaultOptions);
    
    this._context = context;
    
    this.port.onmessage = this._handleProcessorMessage.bind(this);
    
    this.sourcePosition = 0;
    
    this._pendingProcessingTasks = new Map();
    this._nextTaskId = 1;
    
    if (options.pitch !== undefined) {
      this.parameters.get('pitch').value = options.pitch;
    }
    
    if (options.tempo !== undefined) {
      this.parameters.get('tempo').value = options.tempo;
    }
    
    if (options.rate !== undefined) {
      this.parameters.get('rate').value = options.rate;
    }
    
    this._setupEventTarget();
  }

  get pitch() {
    return this.parameters.get('pitch');
  }
  
  get tempo() {
    return this.parameters.get('tempo');
  }
  
  get rate() {
    return this.parameters.get('rate');
  }

  _setupEventTarget() {
    this._eventTarget = new EventTarget();
    this.addEventListener = (type, listener) => 
      this._eventTarget.addEventListener(type, listener);
    this.removeEventListener = (type, listener) => 
      this._eventTarget.removeEventListener(type, listener);
    this.dispatchEvent = (event) => 
      this._eventTarget.dispatchEvent(event);
  }
  
  _handleProcessorMessage(event) {
    const { type, position, taskId, status, progress, processedBuffer, numFrames, numChannels, sampleRate } = event.data;
    
    if (type === 'sourcePosition' && position !== undefined) {
      this.sourcePosition = position;
      this.dispatchEvent(new CustomEvent('positionchange', { 
        detail: { position } 
      }));
    }
    else if (type === 'processingComplete' && taskId !== undefined) {
      const taskResolver = this._pendingProcessingTasks.get(taskId);
      if (taskResolver) {
        if (status === 'success' && processedBuffer) {
          const audioBuffer = this._createAudioBufferFromProcessed(
            processedBuffer, 
            numFrames, 
            numChannels || 2, 
            sampleRate || this._context.sampleRate
          );
          taskResolver(audioBuffer);
        } else {
          taskResolver(null);
        }
        this._pendingProcessingTasks.delete(taskId);
      }
    }
    else if (type === 'processingProgress' && taskId !== undefined && progress !== undefined) {
      this.dispatchEvent(new CustomEvent('processingprogress', {
        detail: { taskId, progress }
      }));
    }
  }
  
  _createAudioBufferFromProcessed(interleavedData, numFrames, numChannels, sampleRate) {
    const buffer = this._context.createBuffer(numChannels, numFrames, sampleRate);
    
    if (numChannels === 1) {
      const channelData = buffer.getChannelData(0);
      for (let i = 0; i < numFrames; i++) {
        channelData[i] = interleavedData[i * 2];
      }
    } else {
      const leftChannel = buffer.getChannelData(0);
      const rightChannel = buffer.getChannelData(1);
      
      for (let i = 0; i < numFrames; i++) {
        leftChannel[i] = interleavedData[i * 2];
        rightChannel[i] = interleavedData[i * 2 + 1];
      }
    }
    
    return buffer;
  }

  processFullBuffer(audioBuffer, options = {}) {
    return new Promise((resolve) => {
      const taskId = this._nextTaskId++;
      
      this._pendingProcessingTasks.set(taskId, resolve);
      
      const processingParams = {
        pitch: options.pitch !== undefined ? options.pitch : this.parameters.get('pitch').value,
        tempo: options.tempo !== undefined ? options.tempo : this.parameters.get('tempo').value,
        rate: options.rate !== undefined ? options.rate : this.parameters.get('rate').value,
        highQuality: options.highQuality !== undefined ? options.highQuality : true
      };
      
      const numChannels = audioBuffer.numberOfChannels;
      const length = audioBuffer.length;
      const sampleRate = audioBuffer.sampleRate;
      
      const interleavedData = new Float32Array(length * 2);
      const leftChannel = audioBuffer.getChannelData(0);
      const rightChannel = numChannels > 1 ? audioBuffer.getChannelData(1) : leftChannel;
      
      for (let i = 0; i < length; i++) {
        interleavedData[i * 2] = leftChannel[i];
        interleavedData[i * 2 + 1] = rightChannel[i];
      }
      
      this.port.postMessage({
        type: 'processFullBuffer',
        taskId,
        buffer: interleavedData,
        sampleRate,
        numChannels,
        params: processingParams,
        returnBuffer: true
      }, [interleavedData.buffer]);
    });
  }

  setPitchSemitones(semitones) {
    this.parameters.get('pitch').value = Math.pow(2, semitones / 12.0);
    return this;
  }
  
  on(eventName, callback) {
    this.addEventListener(eventName, event => callback(event.detail));
    return this;
  }
  
  off(eventName = null) {
    if (eventName) {
      this.removeEventListener(eventName);
    }
    return this;
  }
}